/* Events support inclusive calendar dates and explicit ISO instants, never article dates. */
(function () {
  'use strict';
  function parseInstant(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(value || '');
    if (!match) return NaN;
    const [, y, m, d, h, min, offset] = match;
    const year = Number(y), month = Number(m), day = Number(d);
    if (year < 2000 || month < 1 || month > 12 || day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate() || Number(h) > 23 || Number(min) > 59) return NaN;
    if (offset !== 'Z' && (Number(offset.slice(1, 3)) > 14 || Number(offset.slice(4)) > 59 || (Number(offset.slice(1, 3)) === 14 && Number(offset.slice(4)) !== 0))) return NaN;
    return Date.parse(value);
  }
  function zoneMatches(value, instant, zone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(instant);
      const p = Object.fromEntries(parts.map(part => [part.type, part.value]));
      return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}` === value.slice(0, 16);
    } catch (_) { return false; }
  }

  function parseDay(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return NaN;
    const instant = Date.parse(value + 'T00:00:00Z');
    return Number.isFinite(instant) && new Date(instant).toISOString().slice(0, 10) === value && Number(value.slice(0, 4)) >= 2000 ? instant : NaN;
  }
  function dayAt(now, zone) {
    if (!zone) {
      const d = new Date(now);
      return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
    }
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now).map(p => [p.type, p.value]));
    return p.year + '-' + p.month + '-' + p.day;
  }
  function inspect(event, now) {
    if (!event.title || !event.title.trim()) return { reason: 'incomplete' };
    const allDay = Number.isFinite(parseDay(event.start));
    const start = allDay ? parseDay(event.start) : parseInstant(event.start);
    const endValue = event.end || event.start;
    const end = allDay ? parseDay(endValue) : parseInstant(endValue);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start || (!allDay && event.end && end === start)) return { reason: 'date' };
    let today;
    try { today = dayAt(now, event.zone); } catch (_) { return { reason: 'zone' }; }
    if (!allDay && event.zone && (!zoneMatches(event.start, start, event.zone) || !zoneMatches(endValue, end, event.zone))) return { reason: 'zone' };
    if (!['scheduled', 'rescheduled', 'canceled'].includes(event.status)) return { reason: 'status' };
    if (event.status === 'rescheduled' && !event.confirmed) return { reason: 'unconfirmed' };
    // Dates without times include their final calendar day; never manufacture a public time.
    const startDay = event.start.slice(0, 10);
    let endDay = endValue.slice(0, 10);
    if (!allDay && event.end) {
      const offset = event.end.endsWith('Z') ? 'Z' : event.end.slice(-6);
      const minutes = offset === 'Z' ? 0 : (offset[0] === '-' ? -1 : 1) * (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(4)));
      endDay = new Date(end - 1 + minutes * 60000).toISOString().slice(0, 10);
    }
    if (!allDay && !event.zone) {
      const offset = event.start.endsWith('Z') ? 'Z' : event.start.slice(-6);
      const offsetMinutes = offset === 'Z' ? 0 : (offset[0] === '-' ? -1 : 1) * (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(4)));
      today = new Date(now + offsetMinutes * 60000).toISOString().slice(0, 10);
    }
    if ((allDay || !event.end) ? endDay < today : end <= now) return { reason: 'expired' };
    return { event, start, end, startDay, endDay, allDay, state: event.status === 'canceled' ? 'canceled' : (allDay ? startDay <= today : start <= now) ? 'ongoing' : event.status };
  }
  function agenda(events, now) {
    return events.map(event => inspect(event, now)).filter(item => item.event && item.state !== 'canceled').sort((a, b) => a.start - b.start);
  }
  function select(events, now) { return agenda(events, now).slice(0, 3); }
  function onDay(items, day) { return items.filter(item => item.startDay <= day && item.endDay >= day); }
  // Calendar days already validated by inspect; bounded even for long events.
  function rangeDays(startDay, endDay) {
    const start = parseDay(startDay), end = parseDay(endDay);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
    const count = Math.round((end - start) / 86400000) + 1;
    if (count > 7) return [startDay, endDay];
    return Array.from({ length: count }, (_, index) => new Date(start + index * 86400000).toISOString().slice(0, 10));
  }
  function safeURL(value, base) {
    if (!value || !value.trim() || value.trim().startsWith('#') || /[\u0000-\u0020\\]/.test(value)) return null;
    try { const url = new URL(value, base); return /^https?:$/.test(url.protocol) ? url.href : null; } catch (_) { return null; }
  }

  // Shopify color settings serialize as hex; reject other CSS before assigning styles.
  function eventColor(value) {
    const color = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (/^#[0-9a-f]{6}$/.test(color)) return color;
    if (/^#[0-9a-f]{3}$/.test(color)) return '#' + [...color.slice(1)].map(c => c + c).join('');
    return '#e6001f';
  }
  const automaticColors = ['#e6001f', '#39bde0', '#f4b740', '#a990f5', '#83c86b', '#fa8d62', '#6da7f5', '#eb78bd', '#c9bc5e', '#55c3ad', '#ee6d74', '#9381db', '#d69c62', '#75aadd', '#b7c773', '#dc7eae'];
  function assignEventColors(events) {
    const used = new Set(events.map(event => eventColor(event.color)).filter(color => color !== '#e6001f'));
    let next = 0;
    return events.map(event => {
      const configured = eventColor(event.color);
      if (configured !== '#e6001f') return { ...event, color: configured };
      let color = automaticColors[next % automaticColors.length];
      for (let tries = 0; used.has(color) && tries < automaticColors.length; tries++) color = automaticColors[++next % automaticColors.length];
      used.add(color); next++;
      return { ...event, color };
    });
  }
  function dayTint(items) {
    const colors = items.slice(0, 3).map(({ event }) => {
      const hex = eventColor(event.color).slice(1);
      return 'rgba(' + [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16)).join(',') + ',0.18)';
    });
    // Extra events retain their total count; a neutral segment avoids blending colors.
    if (items.length > 3) colors.push('rgba(255,255,255,0.06)');
    return 'linear-gradient(to right,' + colors.map((color, i) => color + ' ' + (i * 100 / colors.length) + '% ' + ((i + 1) * 100 / colors.length) + '%').join(',') + ')';
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { parseInstant, parseDay, dayAt, zoneMatches, inspect, agenda, select, onDay, safeURL, rangeDays, eventColor, assignEventColors };
  if (typeof document === 'undefined' || window.CollinoEvents) return;
  window.CollinoEvents = { select };
  const roots = new Map();
  const words = {
    es: { scheduled: 'Confirmado', rescheduled: 'Reprogramado', canceled: 'Cancelado: fuera de la agenda.', ongoing: 'En curso', incomplete: 'Falta el título.', date: 'Fecha inválida: usá AAAA-MM-DD o fecha y hora con offset; el fin no puede ser anterior.', zone: 'Zona IANA inválida o distinta del horario indicado.', status: 'Estado inválido.', unconfirmed: 'Confirmá la nueva fecha del evento reprogramado.', expired: 'Finalizado: fuera de la agenda.', empty: 'Próximamente anunciaremos dónde estaremos.', overflow: 'Todos los eventos vigentes permanecen visibles. Al elegir un día, su detalle aparece debajo del calendario; volvé a tocarlo para limpiar la selección.', invalidURL: 'Un enlace inválido fue ocultado.', next: 'Mes siguiente', previous: 'Mes anterior', upcoming: 'Próximos eventos confirmados', noDay: 'No hay eventos confirmados para este día.', noMonth: 'No hay eventos confirmados este mes.', legend: 'Los colores corresponden a cada evento. Tocá una fecha marcada para ver su detalle.', count: 'eventos', calendar: 'Calendario Collino' },
    en: { scheduled: 'Confirmed', rescheduled: 'Rescheduled', canceled: 'Canceled: excluded from the agenda.', ongoing: 'In progress', incomplete: 'Title is missing.', date: 'Invalid date: use YYYY-MM-DD or a date and time with offset; end cannot precede start.', zone: 'Invalid IANA zone or it does not match the time offset.', status: 'Invalid status.', unconfirmed: 'Confirm the new date of this rescheduled event.', expired: 'Ended: excluded from the agenda.', empty: 'We’ll announce where to find us soon.', overflow: 'All current events stay visible. Selecting a day shows its details below the calendar; select it again to clear.', invalidURL: 'An invalid link was hidden.', next: 'Next month', previous: 'Previous month', upcoming: 'Next confirmed events', noDay: 'No confirmed events on this day.', noMonth: 'No confirmed events this month.', legend: 'Colors identify each event. Select a marked date to see details.', count: 'events', calendar: 'Collino calendar' }
  };
  function el(tag, text, className) {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  }
  function init(scope) {
    scope.querySelectorAll('[data-collino-events]').forEach(root => {
      if (roots.has(root)) return;
      const events = assignEventColors(Array.from(root.querySelectorAll('template[data-event]')).map((template, index) => ({ ...template.dataset, confirmed: template.dataset.confirmed === 'true', id: index, template })));
      const language = root.dataset.language === 'en' ? 'en' : 'es', locale = language === 'en' ? 'en-US' : 'es-AR';
      const w = words[language], grid = root.querySelector('[data-events-grid]'), help = root.querySelector('[data-events-help]');
      const calendar = root.querySelector('[data-events-calendar]'), title = root.querySelector('[data-events-list-title]'), reset = root.querySelector('[data-events-reset]'), empty = root.querySelector('[data-events-empty]'), announcement = root.querySelector('[data-events-announcement]');
      const results = root.querySelector('[data-events-results]'), period = root.querySelector('[data-events-period]');
      const selection = root.querySelector('[data-events-selection]');
      root.querySelector('[data-events-extra]')?.remove();
      const controller = new AbortController();
      const expandedCards = new Set();
      let month, chosen = '', previous = '', current = [];
      const initialMonth = () => (current[0]?.state === 'ongoing' ? dayAt(Date.now(), current[0].event.zone) : current[0]?.startDay || dayAt(Date.now())).slice(0, 7);
      const dateLabel = day => new Intl.DateTimeFormat(locale, { timeZone: 'UTC', dateStyle: 'long' }).format(parseDay(day));
      function drawSelection() {
        if (!selection) return;
        const matches = chosen ? onDay(current, chosen) : [];
        selection.hidden = !chosen;
        if (!chosen) { selection.replaceChildren(); return; }
        const heading = el('h4', (language === 'es' ? 'Eventos del ' : 'Events on ') + dateLabel(chosen));
        const details = matches.map(({ event }) => {
          const item = el('div', undefined, 'collino-events__selection-event');
          item.style.setProperty('--event-color', eventColor(event.color));
          item.append(el('strong', event.title));
          if (event.location) item.append(el('p', event.location, 'collino-events__selection-place'));
          const description = event.template.content.querySelector('.collino-events__description');
          const summary = description?.textContent.replace(/\s+/g, ' ').trim();
          if (summary) item.append(el('p', summary.length > 220 ? summary.slice(0, 217).trimEnd() + '…' : summary));
          return item;
        });
        selection.replaceChildren(heading, ...(details.length ? details : [el('p', w.noDay)]));
      }
      function drawCalendar() {
        if (!calendar) return;
        calendar.hidden = false;
        const year = Number(month.slice(0, 4)), m = Number(month.slice(5, 7)) - 1;
        const controls = el('div', undefined, 'collino-events__month');
        const label = el('h3', new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(Date.UTC(year, m, 1)));
        for (const delta of [-1, 1]) {
          const button = el('button', delta < 0 ? '←' : '→'); button.type = 'button'; button.dataset.monthMove = String(delta);
          button.setAttribute('aria-label', delta < 0 ? w.previous : w.next);
          button.disabled = delta < 0 ? year <= 2000 && m === 0 : year >= 9999 && m === 11;
          button.addEventListener('click', () => { month = new Date(Date.UTC(year, m + delta, 1)).toISOString().slice(0, 7); chosen = ''; render(); calendar?.querySelector('[data-month-move="' + delta + '"]').focus({ preventScroll: true }); announcement.textContent = calendar?.querySelector('h3').textContent + '. ' + w.upcoming; });
          if (delta < 0) controls.append(button, label); else controls.append(button);
        }
        const table = el('table'); table.setAttribute('aria-label', w.calendar + ': ' + label.textContent);
        const head = el('thead'), headRow = el('tr');
        for (let d = 0; d < 7; d++) {
          const th = el('th'); th.scope = 'col';
          const abbr = el('abbr', new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short' }).format(Date.UTC(2026, 0, 5 + d)));
          abbr.title = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(Date.UTC(2026, 0, 5 + d)); th.append(abbr); headRow.append(th);
        }
        head.append(headRow); table.append(head);
        const body = el('tbody'), offset = (new Date(Date.UTC(year, m, 1)).getUTCDay() + 6) % 7, days = new Date(Date.UTC(year, m + 1, 0)).getUTCDate();
        let row;
        for (let index = 0; index < 42; index++) {
          if (index % 7 === 0) { row = el('tr'); body.append(row); }
          const cell = el('td'), day = index - offset + 1; row.append(cell);
          if (day < 1 || day > days) continue;
          const key = month + '-' + String(day).padStart(2, '0'), matches = onDay(current, key), count = matches.length;
          const button = el('button'); button.type = 'button'; button.dataset.day = key;
          button.setAttribute('aria-label', dateLabel(key) + ': ' + count + ' ' + w.count + (count ? '. ' + matches.map(item => item.event.title).join('; ') : ''));
          button.setAttribute('aria-pressed', String(chosen === key));
          if (selection?.id) button.setAttribute('aria-controls', selection.id);
          if (key === dayAt(Date.now())) button.setAttribute('aria-current', 'date');
          button.append(el('span', String(day)));
          if (count) {
            button.classList.add('has-events');
            button.style.setProperty('--event-tint', dayTint(matches));
            const badge = el('small', undefined, 'collino-events__day-markers'); badge.setAttribute('aria-hidden', 'true');
            matches.slice(0, 3).forEach(({ event }) => {
              const dot = el('span', undefined, 'collino-events__color-dot');
              dot.style.setProperty('--event-color', eventColor(event.color)); badge.append(dot);
            });
            button.append(badge);
          }
          button.addEventListener('click', () => { chosen = chosen === key ? '' : key; render(); calendar?.querySelector('[data-day="' + key + '"]').focus({ preventScroll: true }); const names = onDay(current, key).map(item => item.event.title); announcement.textContent = chosen ? dateLabel(key) + ': ' + (names.length ? names.join('; ') : w.noDay) : w.upcoming; });
          cell.append(button);
        }
        table.append(body);
        const note = el('p', w.legend, 'collino-events__calendar-note');
        calendar.replaceChildren(controls, table, note);
      }
      function render() {
        const active = document.activeElement, focusedCard = active && root.contains(active) ? active.closest('[data-event-id]') : null;
        const focusedId = focusedCard ? focusedCard.dataset.eventId : null, focusedLink = focusedCard ? active.dataset.eventLink : null;
        // Template content is inert: empty/expired agendas never request a background.
        const backgroundTemplate = root.querySelector('[data-events-background-template]');
        const background = root.querySelector('[data-events-background]');
        if (current.length && backgroundTemplate && !background) backgroundTemplate.after(backgroundTemplate.content.cloneNode(true));
        else if (!current.length && background) background.remove();
        // Keep all configured current cards in place; selecting a day also highlights its cards.
        const selected = root.dataset.eventsView === 'summary' ? current.slice(0, 3) : current;
        const panel = root.querySelector('[data-events-panel]');
        root.dataset.empty = String(!current.length);
        if (panel) {
          panel.hidden = !current.length;
          if (panel.hidden && panel.contains(active)) root.querySelector('[data-events-heading]')?.focus({ preventScroll: true });
        }
        root.dataset.filtered = 'false';
        if (results) results.hidden = true;
        if (period) period.textContent = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(parseDay(initialMonth() + '-01'));
        title.textContent = w.upcoming;
        if (reset) reset.hidden = true;
        empty.hidden = selected.length > 0;
        empty.textContent = w.empty;
        const nodes = selected.map(({ event, state, allDay, startDay, endDay }) => {
          const fragment = event.template.content.cloneNode(true);
          fragment.querySelector('article').dataset.eventId = String(event.id);
          fragment.querySelector('article').style.setProperty('--event-color', eventColor(event.color));
          if (chosen && startDay <= chosen && endDay >= chosen) fragment.querySelector('article').classList.add('is-selected');
          fragment.querySelector('[data-event-status]').dataset.status = state;
          fragment.querySelector('[data-event-status]').textContent = event.status === 'rescheduled' && state === 'ongoing' ? w.rescheduled + ' · ' + w.ongoing : w[state];
          const stamp = fragment.querySelector('[data-event-stamp]');
          if (stamp) {
            const sameMonth = startDay.slice(0, 7) === endDay.slice(0, 7);
            const number = String(Number(startDay.slice(8))) + (sameMonth && endDay !== startDay ? '–' + Number(endDay.slice(8)) : '');
            stamp.setAttribute('aria-hidden', 'true');
            stamp.append(el('span', number, 'collino-events__stamp-day'), el('span', new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'short', year: 'numeric' }).format(parseDay(startDay)), 'collino-events__stamp-month'));
          }
          const range = fragment.querySelector('[data-event-range]');
          if (range) {
            // Full accessible dates/times remain in the info column. This is a visual aid.
            range.setAttribute('aria-hidden', 'true');
            const days = rangeDays(startDay, endDay), list = el('ol');
            const abbreviated = (parseDay(endDay) - parseDay(startDay)) / 86400000 >= 7;
            days.forEach((day, index) => {
              if (index && abbreviated) list.append(el('li', '\u2026', 'collino-events__range-gap'));
              const contextual = abbreviated || startDay.slice(0, 7) !== endDay.slice(0, 7);
              const labelOptions = contextual ? { month: 'short', year: '2-digit' } : { weekday: 'short' };
              const item = el('li'), time = el('time');
              time.append(el('span', new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...labelOptions }).format(parseDay(day))), el('strong', day.slice(8)));
              time.dateTime = day; item.append(time); list.append(item);
            });
            range.append(list);
          }
          const dates = fragment.querySelector('[data-event-date]');
          const values = [event.start]; if (event.end && event.end !== event.start) values.push(event.end);
          values.forEach((value, index) => {
            if (index) dates.append(document.createTextNode(allDay ? '–' : ' - '));
            let label = allDay ? new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' }).format(parseDay(value.slice(0, 10))) : dateLabel(value.slice(0, 10));
            if (allDay && language === 'es' && !index && values.length > 1 && startDay.slice(0, 7) === endDay.slice(0, 7)) label = String(Number(startDay.slice(8)));
            if (!allDay) label = event.zone ? new Intl.DateTimeFormat(locale, { timeZone: event.zone, dateStyle: 'medium', timeStyle: 'short' }).format(parseInstant(value)) : label + ', ' + value.slice(11, 16) + ' (UTC' + (value.endsWith('Z') ? '+00:00' : value.slice(-6)) + ')';
            const time = el('time', label); time.dateTime = value;
            if (allDay) time.setAttribute('aria-label', dateLabel(value.slice(0, 10)));
            dates.append(time);
          });
          fragment.querySelectorAll('[data-event-link]').forEach(link => { const href = safeURL(link.getAttribute('href'), document.baseURI); if (!href) link.remove(); else link.href = href; });
          if (root.dataset.eventsView === 'detail') {
            const card = fragment.querySelector('article'), info = card.querySelector('.collino-events__info');
            const meta = info.querySelector('.collino-events__meta');
            const more = el('div', undefined, 'collino-events__more');
            while (meta.nextSibling) more.append(meta.nextSibling);
            const id = 'collino-event-more-' + (root.closest('.shopify-section')?.id || 'events') + '-' + event.id;
            more.id = id;
            const button = el('button', expandedCards.has(event.id) ? 'Ver menos' : 'Ver más', 'collino-events__toggle');
            button.type = 'button'; button.dataset.eventToggle = String(event.id);
            button.setAttribute('aria-controls', id);
            button.setAttribute('aria-expanded', String(expandedCards.has(event.id)));
            card.dataset.expanded = String(expandedCards.has(event.id));
            info.append(more, button);
          }
          return fragment;
        });
        grid.replaceChildren(...nodes);
        drawCalendar();
        drawSelection();
        if (focusedId !== null && focusedLink) {
          const replacement = root.querySelector('[data-event-id="' + focusedId + '"] [data-event-link="' + focusedLink + '"]');
          (replacement || root.querySelector('[data-events-heading]') || title).focus({ preventScroll: true });
        }
      }
      function update(force) {
        const now = Date.now(); current = agenda(events, now);
        if (!month) month = initialMonth();
        if (help) {
          const messages = events.map((event, index) => { const result = inspect(event, now); const reason = result.reason || (result.state === 'canceled' ? 'canceled' : ''); return reason ? (index + 1) + '. ' + w[reason] : ''; }).filter(Boolean);
          events.forEach((event, index) => { if ([event.detail, event.registration].some(url => url && !safeURL(url, document.baseURI))) messages.push((index + 1) + '. ' + w.invalidURL); });
          help.textContent = (root.dataset.eventsView === 'summary' ? (language === 'es' ? 'Home: próximos 3 eventos. La agenda completa usa estos mismos bloques.' : 'Home: next 3 events. The full calendar uses these same blocks.') : w.overflow) + (messages.length ? ' ' + messages.join(' ') : '');
        }
        const key = dayAt(now) + current.map(item => item.event.id + ':' + item.state).join('|');
        if (key === previous && !force) return;
        previous = key;
        const focusedDay = calendar?.querySelector('button:focus')?.dataset.day, focusedMove = calendar?.querySelector('button:focus')?.dataset.monthMove;
        render();
        if (focusedDay) calendar?.querySelector('[data-day="' + focusedDay + '"]')?.focus({ preventScroll: true });
        else if (focusedMove) calendar?.querySelector('[data-month-move="' + focusedMove + '"]')?.focus({ preventScroll: true });
      }
      // Arrow keys move between dates, Home/End move within the week; Tab remains native.
      grid.addEventListener('click', event => {
        const button = event.target.closest('[data-event-toggle]');
        if (!button || !grid.contains(button)) return;
        const id = Number(button.dataset.eventToggle), card = button.closest('.collino-events__card');
        const open = !expandedCards.has(id);
        if (open) expandedCards.add(id); else expandedCards.delete(id);
        card.dataset.expanded = String(open);
        button.setAttribute('aria-expanded', String(open));
        button.textContent = open ? 'Ver menos' : 'Ver más';
      }, { signal: controller.signal });
      calendar?.addEventListener('keydown', event => {
        const button = event.target.closest('[data-day]'); if (!button) return;
        const day = button.dataset.day, d = new Date(parseDay(day)), weekday = (d.getUTCDay() + 6) % 7;
        const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -weekday, End: 6 - weekday }[event.key];
        if (delta === undefined) return; event.preventDefault();
        const next = new Date(d.getTime() + delta * 86400000).toISOString().slice(0, 10);
        if (next.slice(0, 4) < '2000' || next.slice(0, 4) > '9999') return;
        if (month !== next.slice(0, 7)) { month = next.slice(0, 7); chosen = ''; render(); } else drawCalendar(); calendar?.querySelector('[data-day="' + next + '"]')?.focus();
      }, { signal: controller.signal });
      update.dispose = () => controller.abort();
      roots.set(root, update); update(true);
    });
  }
  function refresh() { roots.forEach((update, root) => { if (root.isConnected) update(); else { update.dispose(); roots.delete(root); } }); }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { roots.forEach((update, root) => { if (event.target.contains(root)) { update.dispose(); roots.delete(root); } }); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  window.addEventListener('pageshow', refresh);
  setInterval(refresh, 30000);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true }); else init(document);
})();
